import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

export const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Image with Overlay Effects */}
      <div className="absolute inset-0">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(/homebg/home%20bg.png)' }}
        />

        {/* Dark Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40" />

        {/* Animated Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/60 via-blue-900/40 to-slate-900/60" />

        {/* Subtle Animated Gradient Mesh */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-[700px] h-[700px] bg-gradient-to-br from-primary/30 to-primary-glow/20 rounded-full blur-3xl animate-gradient-shift" />
          <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-gradient-to-tl from-accent/40 to-accent-dark/20 rounded-full blur-3xl animate-gradient-shift-reverse" />
        </div>

        {/* Floating Particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-primary/30 rounded-full animate-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${15 + Math.random() * 10}s`,
              }}
            />
          ))}
          {[...Array(15)].map((_, i) => (
            <div
              key={`accent-${i}`}
              className="absolute w-1.5 h-1.5 bg-accent/40 rounded-full animate-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${12 + Math.random() * 8}s`,
              }}
            />
          ))}
        </div>

        {/* Rotating Geometric Shapes */}
        <div className="absolute top-20 right-20 w-40 h-40 border-4 border-accent/20 rounded-full animate-spin-slow" />
        <div className="absolute top-32 right-32 w-24 h-24 border-2 border-primary/30 rounded-full animate-spin-reverse" />
        <div className="absolute bottom-40 left-40 w-32 h-32 border-4 border-primary/20 rounded-full animate-float-rotate" />
        <div className="absolute bottom-52 left-52 w-20 h-20 border-2 border-accent/25 rounded-full animate-float-rotate" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/3 left-10 w-16 h-16 border-3 border-primary-glow/25 rotate-45 animate-pulse-slow" />
        <div className="absolute bottom-1/3 right-10 w-20 h-20 border-3 border-accent/20 rotate-12 animate-pulse-slow" style={{ animationDelay: '1.5s' }} />

        {/* Animated Lines */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent animate-slide-down" />
        <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-accent/20 to-transparent animate-slide-down" style={{ animationDelay: '2s' }} />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto text-center space-y-8">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 backdrop-blur-sm shadow-lg">
              <Sparkles className="h-4 w-4 text-accent animate-pulse-slow" />
              <span className="text-sm font-medium bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Established Since 2015</span>
            </div>
          </motion.div>

          {/* Main Heading */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-0"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-none mb-0">
              <span className="gradient-text-shimmer">VTV ENTERPRISES</span>
              <br />
              <span className="text-white text-lg sm:text-xl md:text-2xl lg:text-3xl inline-block -translate-y-4 sm:-translate-y-6 md:-translate-y-8 lg:-translate-y-10">
                (PVT) LTD
              </span>
            </h1>

            {/* Tagline */}
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto mt-4">
              A trusted name in business excellence, serving customers with dedication and professionalism since 2015.
            </p>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
          >
            <Button asChild size="lg" className="bg-accent hover:bg-accent-dark glow-red group">
              <Link to="/contact" className="flex items-center gap-2">
                Contact Us
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
              <Link to="/services">View Services</Link>
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="pt-12 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-3xl mx-auto"
          >
            {[
              { label: 'Years of Experience', value: '9+', color: 'from-cyan-400 to-blue-500' },
              { label: 'Customer Satisfaction', value: '100%', color: 'from-yellow-400 to-orange-500' },
              { label: 'Support Channels', value: '6+', color: 'from-green-400 to-emerald-500' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.8 + index * 0.1 }}
                className="glass-card p-6 rounded-xl hover-lift group border border-white/20"
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
                }}
              >
                <p className="text-3xl sm:text-4xl font-bold mb-2 text-black">
                  {stat.value}
                </p>
                <p className="text-sm text-black font-bold">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
        >
          <path
            d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z"
            fill="hsl(var(--background))"
          />
        </svg>
      </div>
    </section>
  );
};
